"use client";
import Link from "next/link";
import { type CSSProperties, type ReactNode } from "react";
import clsx from "clsx";

const BASE =
  "rounded-2xl border border-gray-200 bg-white shadow-sm";

const INTERACTIVE =
  "cursor-pointer hover:shadow-md hover:border-primaryColor transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primaryColor focus-visible:ring-offset-1";

interface ClickableCardProps {
  children: ReactNode;
  /** Padding class – defaults to "p-5" */
  padding?: string;
  className?: string;
  /** Inline styles – useful for CSS custom-property values (e.g. DaisyUI color tokens) */
  style?: CSSProperties;
  /** Accessible label forwarded as `aria-label` on the rendered element.
   *  React prop interfaces cannot use hyphenated names, so camelCase is used here. */
  ariaLabel?: string;
  /** When set the card renders as a <Link> */
  href?: string;
  /** When set (and no href) the card renders as a keyboard-accessible div */
  onClick?: () => void;
  disabled?: boolean;
  /** Tooltip text shown on hover when the card is disabled */
  disabledTooltip?: string;
}

export function ClickableCard({
  children,
  padding = "p-5",
  className,
  style,
  ariaLabel,
  href,
  onClick,
  disabled,
  disabledTooltip,
}: ClickableCardProps) {
  const isClickable = (href || onClick) && !disabled;

  const classes = clsx(
    BASE,
    padding,
    isClickable && INTERACTIVE,
    disabled && "opacity-50 cursor-not-allowed",
    className
  );

  if (href && !disabled) {
    return (
      <Link href={href} className={classes} style={style} aria-label={ariaLabel}>
        {children}
      </Link>
    );
  }

  if (onClick && !disabled) {
    return (
      <div
        role="button"
        tabIndex={0}
        className={classes}
        style={style}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        aria-label={ariaLabel}
      >
        {children}
      </div>
    );
  }

  const card = (
    <div className={classes} style={style} aria-label={ariaLabel}>
      {children}
    </div>
  );

  if (disabled && disabledTooltip) {
    return (
      <div className="tooltip tooltip-top tooltip-warning" data-tip={disabledTooltip}>
        {card}
      </div>
    );
  }

  return card;
}
